import { createSlice } from '@reduxjs/toolkit';
import axios from 'utils/axios';
import { dispatch } from 'redux_data/store';

// ============================================
// 🔷 INITIAL STATE
// ============================================
const initialState = {
  isLoading: false,
  error: null,
  config: null,
};

// ============================================
// 🔷 SLICE
// ============================================
const slice = createSlice({
  name: 'default_coa',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.hasError = null;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.hasError = typeof action.payload === 'string'
        ? action.payload
        : JSON.stringify(action.payload);
    },

    setConfig(state, action) {
      state.isLoading = false;
      state.config = action.payload;
      state.hasError = null;
    },

    unsetError(state) {
      state.hasError = null;
    },

    resetState() {
      return initialState;
    },
  },
});

export default slice.reducer;
export const { unsetError } = slice.actions;
// ============================================
// 🔷 ACTIONS
// ============================================

/**
 * 🔹 Fetch (GET /default_coa)
 * BE already guarantees data exists
 */
export function fetchData() {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const res = await axios.get('/default_coa');

      if (res.data?.success) {
        const config = res.data?.data?.config || {};
        dispatch(slice.actions.setConfig(config));
      } else {
        dispatch(slice.actions.hasError(res.data));
      }

    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || err));
    }
  };
}

/**
 * 🔹 Upsert full config (POST /default_coa)
 * rarely needed, but kept for flexibility
 */
export function upsertData({ config, encryptedPin }) {
  return async () => {
    dispatch(slice.actions.startLoading());

    if (encryptedPin) {
      axios.defaults.headers.common.pin = encryptedPin;
    }

    try {
      const res = await axios.post('/default_coa', { config });

      if (res.data?.success) {
        dispatch(slice.actions.setConfig(res.data?.data?.config || config));
      } else {
        dispatch(slice.actions.hasError(res.data));
      }

    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || err));
    }

    delete axios.defaults.headers.common.pin;
  };
}

/**
 * 🔹 Update partial config (PATCH /default_coa/config)
 * main action used in UI
 */
export function updateData({ config, encryptedPin }) {
  return async () => {
    dispatch(slice.actions.startLoading());

    if (encryptedPin) {
      axios.defaults.headers.common.pin = encryptedPin;
    }

    try {
      const res = await axios.patch('/default_coa/config', {
        config,
      });

      if (res.data?.success) {
        dispatch(slice.actions.setConfig(res.data?.data?.config || config));
      } else {
        dispatch(slice.actions.hasError(res.data));
      }

    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || err));
    }

    delete axios.defaults.headers.common.pin;
  };
}

/**
 * 🔹 Reset (logout, etc)
 */
export function resetData() {
  return (dispatch) => {
    dispatch(slice.actions.resetState());
  };
}